import React from 'react';
import { Building2, FolderKanban, Table as TableIcon, Database, HardDrive, Copy, Check } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { AdminOrganization, Project } from '../../../../types/domain';
import { formatBytes } from '../types';

interface AdminOrgHeaderProps {
  organization: AdminOrganization;
  projects: Project[];
  totalDatastores: number;
  totalSubmissions: number;
  totalStorageBytes: number;
  copiedId: boolean;
  onCopyId: (id: string) => void;
}

export const AdminOrgHeader: React.FC<AdminOrgHeaderProps> = ({
  organization,
  projects,
  totalDatastores,
  totalSubmissions,
  totalStorageBytes,
  copiedId,
  onCopyId,
}) => {
  return (
    <>
      {/* Organization Identity Card */}
      <div className="bg-cf-surface border border-cf-border rounded-lg p-5 shadow-xs font-sans text-xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-cf-orange/10 border border-cf-orange/30 flex items-center justify-center shrink-0">
              <Building2 size={20} className="text-cf-orange" />
            </div>
            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-lg font-bold text-cf-textPrimary tracking-tight">
                  {organization.name}
                </h1>
                <span className="px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider bg-cf-orange/10 text-cf-orange border border-cf-orange/30 font-sans">
                  {organization.plan_tier || 'FREE'}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs text-cf-textMuted mt-1 flex-wrap font-sans">
                <span>Slug: <strong className="text-cf-textSecondary">{organization.slug}</strong></span>
                <span>•</span>
                <div className="flex items-center gap-1">
                  <span>ID: <code className="font-sans text-xs">{organization.id}</code></span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => onCopyId(organization.id)}
                    title="Copy Organization UUID"
                    className="h-auto p-0.5 text-cf-textMuted hover:text-cf-textPrimary"
                  >
                    {copiedId ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
                  </Button>
                </div>
                <span>•</span>
                <span>Owner: <strong className="text-cf-textSecondary">{organization.owner_email || '—'}</strong></span>
                <span>•</span>
                <span>Created: {new Date(organization.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bento Stats Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 font-sans text-xs">
        <div className="bg-cf-surface border border-cf-border rounded-lg p-4 shadow-xs">
          <div className="flex items-center justify-between text-cf-textSecondary text-xs mb-2">
            <span className="uppercase text-xs tracking-wider font-medium">Projects</span>
            <FolderKanban size={15} className="text-cf-orange" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-cf-textPrimary">{projects.length}</span>
            <span className="text-xs text-cf-textSecondary">active</span>
          </div>
        </div>

        <div className="bg-cf-surface border border-cf-border rounded-lg p-4 shadow-xs">
          <div className="flex items-center justify-between text-cf-textSecondary text-xs mb-2">
            <span className="uppercase text-xs tracking-wider font-medium">Datastores</span>
            <TableIcon size={15} className="text-blue-500" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-cf-textPrimary">{totalDatastores}</span>
            <span className="text-xs text-cf-textSecondary">tables</span>
          </div>
        </div>

        <div className="bg-cf-surface border border-cf-border rounded-lg p-4 shadow-xs">
          <div className="flex items-center justify-between text-cf-textSecondary text-xs mb-2">
            <span className="uppercase text-xs tracking-wider font-medium">Submissions</span>
            <Database size={15} className="text-emerald-500" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-cf-textPrimary">
              {totalSubmissions.toLocaleString()}
            </span>
            <span className="text-xs text-cf-textSecondary">rows</span>
          </div>
        </div>

        <div className="bg-cf-surface border border-cf-border rounded-lg p-4 shadow-xs">
          <div className="flex items-center justify-between text-cf-textSecondary text-xs mb-2">
            <span className="uppercase text-xs tracking-wider font-medium">Storage Used</span>
            <HardDrive size={15} className="text-purple-500" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-cf-textPrimary">
              {formatBytes(totalStorageBytes)}
            </span>
            <span className="text-xs text-cf-textSecondary">footprint</span>
          </div>
        </div>
      </div>
    </>
  );
};
